/**
 * THE MONTHLY SUBSCRIPTION — $4.99/month, and the two things it buys: 3× Petals on every grant, and the
 * ability to CLAIM achievement reward items. This file is the whole entitlement: the persisted state, the
 * one `isSubscriber` selector everything else reads, the multiplier, and the player-facing strings.
 *
 * NO PAYMENT PROVIDER IS CALLED FROM HERE, deliberately. Everything below is truthful about a subscription
 * whoever writes it — the Stripe webhook on the server, or the dev toggle — and nothing in this file knows
 * which. The network calls live in sim/membership.ts; the seam is described at the bottom of the file.
 *
 * MONEY IS INTEGER MINOR UNITS, exactly as sim/payments.ts does it: the price is 499 in code and becomes
 * "$4.99" only at the display edge, through `formatPrice`. Never introduce a 4.99 float.
 *
 * THE RULE THAT SHAPES THE FILE: A NON-SUBSCRIBER LOSES NOTHING.
 * Achievements, badges and achievement POINTS are untouched by this file — a non-subscriber earns all of
 * them at exactly the rate they always did. What a subscription gates is the CLAIM of a reward item, and
 * qualification is stored separately from the grant so that gate can never destroy anything:
 *
 *   • QUALIFICATION is derived from the achievement-point score, every read, and is not stored at all.
 *     A rung the player has reached stays reached whether or not they ever pay.
 *   • THE GRANT is `claimedMilestones` — written only when a subscriber clicks. Nothing is auto-consumed,
 *     nothing expires, and nothing is marked "missed".
 *
 * So the day a player subscribes, every rung they ever qualified for is simply claimable, because it always
 * was — the only thing that changed is the gate in front of the button. The counts that say so out loud are
 * the track's own (`qualifiedUnclaimedMilestones`, `unclaimedBehindCount`), which is where they belong: they
 * are questions about the track, and a second count living here could only drift from it. That is also why
 * the claim is idempotent for free: `claimMilestone` already refuses an id in the claimed set, and this gate
 * sits in FRONT of that check rather than replacing it.
 *
 * TONE, which is a requirement and not a preference. A non-subscriber is never told they missed out, lost
 * something, or failed to qualify. Their rewards are WAITING. Every string in `SUB_COPY` is written from
 * that side — "unlock your rewards", never "you didn't get this" — and they are short because a paywall
 * that explains itself at length reads as an apology. Add strings here, not in the modals, so the wording
 * stays in one place when the upsell modal is built.
 */

import { formatPrice } from "./payments";

/** The subscription price in integer CENTS. Display with `formatPrice(SUBSCRIPTION_PRICE_CENTS)`. */
export const SUBSCRIPTION_PRICE_CENTS = 499;

/** What every Petals grant is multiplied by while the subscription is active. */
export const SUBSCRIBER_PETAL_MULTIPLIER = 3;

/**
 * WHERE A SUBSCRIPTION STANDS.
 *   • `none`    — never subscribed (or fully wound down). The default for every save.
 *   • `active`  — paid and inside the period. `isSubscriber` is true.
 *   • `lapsed`  — was subscribed, the period ended, not renewed. Benefits OFF, but the state is kept so the
 *                 player is greeted as someone coming back rather than as a new prospect.
 *
 * `lapsed` is a distinct status rather than a return to `none` because the two want different copy and
 * because losing the fact of a past subscription is the kind of thing a support ticket is made of.
 */
export type SubscriptionStatus = "none" | "active" | "lapsed";

/**
 * The persisted subscription. Absent from a save entirely = never subscribed, which is why every reader
 * goes through `isSubscriber` / `subscriptionStatus` rather than touching the field.
 *
 * `periodEnd` is ms epoch — the moment the CURRENT paid period runs out. It is what makes `active` decay to
 * `lapsed` on its own: the stored status is what the provider last told us, and `subscriptionStatus()`
 * re-reads it against the clock, so a save that sat untouched for two months does not come back entitled.
 */
export interface SubscriptionState {
  status: SubscriptionStatus;
  /** ms epoch the current paid period ends. Undefined on `none`. */
  periodEnd?: number;
  /** The provider's subscription id, once there is a provider. Opaque here; never parsed. Its presence is
   *  what marks a subscription as PAID FOR rather than dev-toggled — the server owns any state carrying one. */
  providerId?: string;
  /** The member asked to stop: still `active` until `periodEnd`, then it lapses instead of renewing. Absent
   *  = renews. Display only — the clock, not this flag, is what ends the benefits. */
  cancelAtPeriodEnd?: boolean;
}

/** A save with no subscription field at all. */
export const NO_SUBSCRIPTION: SubscriptionState = { status: "none" };

/** Defensively read a stored subscription (untrusted JSON → a state that always answers). */
export function coerceSubscription(raw: unknown): SubscriptionState {
  if (!raw || typeof raw !== "object") return { ...NO_SUBSCRIPTION };
  const o = raw as Partial<SubscriptionState>;
  const status: SubscriptionStatus =
    o.status === "active" || o.status === "lapsed" ? o.status : "none";
  const end = typeof o.periodEnd === "number" && Number.isFinite(o.periodEnd) && o.periodEnd > 0
    ? Math.round(o.periodEnd) : undefined;
  const providerId = typeof o.providerId === "string" && o.providerId ? o.providerId : undefined;
  return {
    status, ...(end !== undefined ? { periodEnd: end } : {}), ...(providerId ? { providerId } : {}),
    ...(o.cancelAtPeriodEnd === true ? { cancelAtPeriodEnd: true } : {}),
  };
}

/**
 * THE EFFECTIVE status, which is the stored one aged against the clock: an `active` subscription whose
 * `periodEnd` has passed reads as `lapsed`. Pass `now` (the game clock) so this stays testable and so the
 * whole app agrees on one time source.
 *
 * An `active` state with NO `periodEnd` stays active — that is the dev toggle's shape, and a subscription
 * with no end date is exactly what "on until someone turns it off" means.
 */
export function subscriptionStatus(sub: SubscriptionState | undefined, now: number): SubscriptionStatus {
  const s = sub?.status ?? "none";
  if (s !== "active") return s;
  if (sub?.periodEnd !== undefined && now >= sub.periodEnd) return "lapsed";
  return "active";
}

/** THE SELECTOR. Every benefit in the game reads this one function and nothing else. */
export function isSubscriber(sub: SubscriptionState | undefined, now: number): boolean {
  return subscriptionStatus(sub, now) === "active";
}

/**
 * THE PETALS FUNNEL. Every grant of Petals in the game goes through here — check-in claims, achievement
 * payouts, anything added later — and a subscriber's is tripled.
 *
 * THIS TAKES THE BOOLEAN, NOT THE STATE, on purpose: the caller has already resolved `isSubscriber` once
 * for the render, and a grant function that re-reads the clock is a grant function whose result depends on
 * when you call it. Rounds DOWN so the multiplier can never mint a fractional Petal.
 */
export function grantedPetals(base: number, subscriber: boolean): number {
  if (!(base > 0)) return 0;
  return Math.floor(base * (subscriber ? SUBSCRIBER_PETAL_MULTIPLIER : 1));
}

/**
 * THE STRINGS, written here rather than in the modals so the tone is decided in one place and the website
 * (site/GardenClub.tsx, the Profile panel) inherits it instead of re-inventing it. Short on purpose. Nothing in here tells a
 * player what they lost; the reward is always something waiting for them.
 */
export const SUB_COPY = {
  /** The product name, as the player sees it. */
  name: "Garden Club",
  /** Price line, e.g. "$4.99/month". Derived so the number lives in exactly one place. */
  price: `${formatPrice(SUBSCRIPTION_PRICE_CENTS)}/month`,
  /** The benefits, in the order they are worth. */
  perks: [
    `${SUBSCRIBER_PETAL_MULTIPLIER}× Petals on everything you earn`,
    "Collect every Garden Club prize your achievements carry",
    "Unlimited Petal exchange, with no daily limit",
  ],
  /** The button that starts a subscription. */
  cta: "Join Garden Club",
  /** On a reward the player has qualified for but cannot yet claim. */
  lockedReward: "Waiting for you",
  /** Under a held reward, one line. */
  lockedHint: "Claim it with Garden Club.",
  /** The count line above a pile of held rewards. `n` is always ≥ 1 where this is used. */
  heldCount: (n: number) => `${n} reward${n === 1 ? "" : "s"} waiting for you`,
  /** Shown the moment a subscription turns on and rewards become claimable. */
  unlocked: "Your rewards are ready. Claim them any time.",
  /** For a lapsed subscriber. Still not a scolding: the rewards went back to waiting, they did not vanish. */
  lapsed: "Your rewards are safe here whenever you come back.",
  /** The member's own headline, wherever a non-member would see the pitch. */
  memberHeadline: "You're a Garden Club member",
  /** Under it: what membership is doing for them right now. */
  memberLine: `Your ${SUBSCRIBER_PETAL_MULTIPLIER}× Petals and achievement prizes are switched on.`,
  /** A member who has asked to stop, until the period runs out. `date` is already formatted. */
  endingLine: (date: string) => `Your membership stays active until ${date}, then ends. You keep everything you've collected.`,
} as const;

// ── THE PAYMENT SEAM ───────────────────────────────────────────────────────────────────────────────────────
//
// EVERYTHING ABOVE THIS LINE IS THE ENTITLEMENT and does not know where the state came from. Stripe is wired
// in two places, neither of them here:
//
//   1. THE SERVER (server/index.js, "GARDEN CLUB") is the ONLY writer of a paid `SubscriptionState`. Stripe's
//      `checkout.session.completed` and `customer.subscription.*` webhooks re-read the subscription from
//      Stripe and write `status` / `periodEnd` / `providerId` / `cancelAtPeriodEnd` onto the player's save,
//      and a save PUT can never overwrite a state carrying a `providerId`. The client never grants itself a
//      subscription from a checkout redirect — the redirect means "the browser came back", not "it cleared".
//
//   2. THE CLIENT CALLS (sim/membership.ts): start checkout, cancel / resume, open Stripe's billing page, and
//      read the server's view of the membership. The game adopts that view on load and whenever a site tab
//      announces a change (`SUBSCRIPTION_CHANNEL`), so a member who joined on the website is a member in a
//      game tab that was already open.
//
// Without Stripe keys (local dev) the server takes the save's `subscription` from the client, so the dev
// toggle (DevMenu → "Subscription") still exercises every gate through the real code path; with keys, only
// an admin's dev toggle is honoured, and never over a paid subscription.

/** The dev/webhook writer: the state an ACTIVE subscription has. `periodEnd` omitted = no expiry, which is
 *  what the dev toggle wants; the webhook passes the period end Stripe reports. */
export function activeSubscription(periodEnd?: number, providerId?: string): SubscriptionState {
  return { status: "active", ...(periodEnd !== undefined ? { periodEnd } : {}), ...(providerId ? { providerId } : {}) };
}
